import {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { App, Index } from "dwv";
import Loading from "../Loading";
import {
  Bars3Icon,
  AdjustmentsVerticalIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { HandIcon, RefreshCwIcon, RulerIcon } from "lucide-react";

export interface DicomViewerHandle {
  resetZoom: () => void;
  fitToWindow: () => void;
  getCurrentSlice: () => number;
  setSlice: (slice: number) => void;
  getTotalSlices: () => number;
  getCurrentFrame: () => number;
  setFrame: (frame: number) => void;
  getTotalFrames: () => number;
  setTool: (tool: string) => void;
  toggleOrientation: () => void;
}

interface DicomViewerProps {
  url: string;
  width?: number | string;
  height?: number | string;
  heightOffset?: number;
  showTools?: boolean;
  showToolbar?: boolean;
  onImageLoad?: (info: {
    width: number;
    height: number;
    slices: number;
  }) => void;
  onError?: (error: Error) => void;
}

type ToolName = "Scroll" | "ZoomAndPan" | "WindowLevel" | "Draw";
type Orientation = "axial" | "coronal" | "sagittal";

const TOOLS_CONFIG = {
  Scroll: { options: [] },
  ZoomAndPan: { options: [] },
  Draw: { options: ["Ruler"] },
};

const getToolIcon = (tool: ToolName) => {
  const iconProps = { className: "w-5 h-5" };
  switch (tool) {
    case "Scroll":
      return <Bars3Icon {...iconProps} />;
    case "ZoomAndPan":
      return <HandIcon {...iconProps} />;
    case "WindowLevel":
      return <AdjustmentsVerticalIcon {...iconProps} />;
    case "Draw":
      return <RulerIcon {...iconProps} />;
    default:
      return null;
  }
};

export const DicomViewer = forwardRef<DicomViewerHandle, DicomViewerProps>(
  function DicomViewer(props, ref) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [app, setApp] = useState<App | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [loadProgress, setLoadProgress] = useState(0);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [selectedTool, setSelectedTool] = useState<ToolName>("ZoomAndPan");
    const [canScroll, setCanScroll] = useState(false);
    const [canWindowLevel, setCanWindowLevel] = useState(false);
    const [orientation, setOrientation] = useState<Orientation>("axial");
    const [showDicomTags, setShowDicomTags] = useState(false);
    const [metaData, setMetaData] = useState<any>({});
    const [imageInfo, setImageInfo] = useState<{
      width: number;
      height: number;
      slices: number;
    } | null>(null);
    // Dimension 2 - Slices (spatial depth)
    const [currentSlice, setCurrentSlice] = useState(0);
    const [totalSlices, setTotalSlices] = useState(1);
    // Dimension 3 - Frames (temporal/time)
    const [currentFrame, setCurrentFrame] = useState(0);
    const [totalFrames, setTotalFrames] = useState(1);

    useImperativeHandle(
      ref,
      () => ({
        resetZoom: () => {
          if (app) {
            app.resetLayout();
          }
        },
        fitToWindow: () => {
          if (app) {
            app.resetLayout();
          }
        },
        getCurrentSlice: () => {
          return currentSlice;
        },
        setSlice: (slice: number) => {
          if (!app || !dataLoaded) return;
          const clampedSlice = Math.max(0, Math.min(slice, totalSlices - 1));
          try {
            const dataIds = app.getDataIds();
            if (dataIds.length > 0) {
              const vl = app.getViewLayersByDataId(dataIds[0])[0];
              const vc = vl.getViewController();
              const index = vc.getCurrentIndex();
              const values = index.getValues();
              values[2] = clampedSlice; // Dimension 2 = slices
              vc.setCurrentIndex(new Index(values));
            }
          } catch (err) {
            console.error("Failed to navigate to slice:", err);
          }
        },
        getTotalSlices: () => {
          return totalSlices;
        },
        getCurrentFrame: () => {
          return currentFrame;
        },
        setFrame: (frame: number) => {
          if (!app || !dataLoaded) return;
          const clampedFrame = Math.max(0, Math.min(frame, totalFrames - 1));
          try {
            const dataIds = app.getDataIds();
            if (dataIds.length > 0) {
              const vl = app.getViewLayersByDataId(dataIds[0])[0];
              const vc = vl.getViewController();
              const index = vc.getCurrentIndex();
              const values = index.getValues();
              values[3] = clampedFrame; // Dimension 3 = frames
              vc.setCurrentIndex(new Index(values));
            }
          } catch (err) {
            console.error("Failed to navigate to frame:", err);
          }
        },
        getTotalFrames: () => {
          return totalFrames;
        },
        setTool: (tool: string) => {
          handleToolChange(tool as ToolName);
        },
        toggleOrientation: () => {
          handleToggleOrientation();
        },
      }),
      [
        app,
        imageInfo,
        orientation,
        currentSlice,
        currentFrame,
        totalSlices,
        totalFrames,
        dataLoaded,
      ],
    );

    const canRunTool = (tool: ToolName): boolean => {
      switch (tool) {
        case "Scroll":
          return canScroll;
        case "WindowLevel":
          return canWindowLevel;
        default:
          return true;
      }
    };

    const handleToolChange = (tool: ToolName) => {
      if (app && dataLoaded) {
        setSelectedTool(tool);
        app.setTool(tool);
        if (tool === "Draw") {
          app.setToolFeatures({ shapeName: "Ruler" });
        } else {
          // Reset to view layer if draw was active
          const lg = app.getActiveLayerGroup();
          lg?.setActiveLayer(0);
        }
      }
    };

    const handleReset = () => {
      if (app) {
        app.resetLayout();
      }
    };

    const handleToggleOrientation = () => {
      if (!app || !dataLoaded) return;

      let newOrientation: Orientation;
      switch (orientation) {
        case "axial":
          newOrientation = "coronal";
          break;
        case "coronal":
          newOrientation = "sagittal";
          break;
        case "sagittal":
          newOrientation = "axial";
          break;
        default:
          newOrientation = "coronal";
      }

      setOrientation(newOrientation);

      // Update data view config
      const config = {
        "*": [
          {
            divId: "layerGroup0",
            orientation: newOrientation,
          },
        ],
      };
      // @ts-ignore
      app.setDataViewConfigs(config);

      // Re-render data
      const dataIds = app.getDataIds();
      for (const dataId of dataIds) {
        app.render(dataId);
      }
    };

    // Navigation for Slices (dimension 2)
    const goToSlice = (targetSlice: number) => {
      if (!app || !dataLoaded) return;
      const clampedSlice = Math.max(0, Math.min(targetSlice, totalSlices - 1));

      try {
        const dataIds = app.getDataIds();
        if (dataIds.length > 0) {
          const vl = app.getViewLayersByDataId(dataIds[0])[0];
          const vc = vl.getViewController();
          const index = vc.getCurrentIndex();
          const values = index.getValues();
          values[2] = clampedSlice; // Dimension 2 = slices
          vc.setCurrentIndex(new Index(values));
        }
      } catch (err) {
        console.error("Failed to navigate to slice:", err);
      }
    };

    const handlePreviousSlice = () => {
      if (currentSlice > 0) {
        goToSlice(currentSlice - 1);
      }
    };

    const handleNextSlice = () => {
      if (currentSlice < totalSlices - 1) {
        goToSlice(currentSlice + 1);
      }
    };

    // Navigation for Frames (dimension 3)
    const goToFrame = (targetFrame: number) => {
      if (!app || !dataLoaded) return;
      const clampedFrame = Math.max(0, Math.min(targetFrame, totalFrames - 1));

      try {
        const dataIds = app.getDataIds();
        if (dataIds.length > 0) {
          const vl = app.getViewLayersByDataId(dataIds[0])[0];
          const vc = vl.getViewController();
          const index = vc.getCurrentIndex();
          const values = index.getValues();
          values[3] = clampedFrame; // Dimension 3 = frames
          vc.setCurrentIndex(new Index(values));
        }
      } catch (err) {
        console.error("Failed to navigate to frame:", err);
      }
    };

    const handlePreviousFrame = () => {
      if (currentFrame > 0) {
        goToFrame(currentFrame - 1);
      }
    };

    const handleNextFrame = () => {
      if (currentFrame < totalFrames - 1) {
        goToFrame(currentFrame + 1);
      }
    };

    useEffect(() => {
      const container = containerRef.current;
      if (!container || !props.url) {
        return;
      }

      // Reset all state when URL changes
      setIsLoading(true);
      setError(null);
      setLoadProgress(0);
      setDataLoaded(false);
      setSelectedTool("ZoomAndPan");
      setCanScroll(false);
      setCanWindowLevel(false);
      setMetaData({});
      setImageInfo(null);
      setCurrentSlice(0);
      setTotalSlices(1);
      setCurrentFrame(0);
      setTotalFrames(1);

      const initializeViewer = async () => {
        try {
          // Create a unique container ID to avoid conflicts
          const containerId =
            "layerGroup-" + Math.random().toString(36).substr(2, 9);
          container.id = containerId;

          // Clean up any existing content
          container.innerHTML = "";

          // Create new DWV app
          const dwvApp = new App();

          // Initialize the app
          dwvApp.init({
            // @ts-ignore
            dataViewConfigs: { "*": [{ divId: containerId }] },
            tools: TOOLS_CONFIG,
          });

          // Set up event listeners
          let nLoadItem = 0;
          let nReceivedLoadError = 0;
          let isFirstRender = true;

          dwvApp.addEventListener("loadstart", () => {
            nLoadItem = 0;
            nReceivedLoadError = 0;
            isFirstRender = true;
            setLoadProgress(0);
          });

          dwvApp.addEventListener("loadprogress", (event: any) => {
            setLoadProgress(event.loaded);
          });

          dwvApp.addEventListener("renderend", (event: any) => {
            if (isFirstRender) {
              isFirstRender = false;
              const vl = dwvApp.getViewLayersByDataId(event.dataid)[0];
              const vc = vl.getViewController();

              // Get the actual image size from view controller
              try {
                const imageSize = vc.getImageSize();
                const imageSizeValues = imageSize.getValues();

                // Set both dimensions independently
                const slices = imageSizeValues[2] || 1;
                const frames = imageSizeValues[3] || 1;

                setTotalSlices(slices);
                setTotalFrames(frames);
              } catch (err) {
                console.warn(
                  "Could not get image size from ViewController:",
                  err,
                );
              }

              // Check available tools
              if (vc.canScroll()) {
                setCanScroll(true);
              }
              if (vc.isMonochrome()) {
                setCanWindowLevel(true);
              }

              // Set initial tool
              let initialTool: ToolName = "ZoomAndPan";
              if (vc.canScroll()) {
                initialTool = "Scroll";
              }
              setSelectedTool(initialTool);
              dwvApp.setTool(initialTool);
            }
          });

          dwvApp.addEventListener("load", (event: any) => {
            setMetaData(dwvApp.getMetaData(event.dataid));
            setLoadProgress(100);
            setDataLoaded(true);
            setIsLoading(false);

            // Get image information
            try {
              const dataIds = dwvApp.getDataIds();
              if (dataIds.length > 0) {
                const data = dwvApp.getData(dataIds[0]);
                if (data && data.image) {
                  const image = data.image;
                  const geometry = image.getGeometry();

                  const geometrySize = geometry.getSize();

                  const info = {
                    width: geometrySize.get(0),
                    height: geometrySize.get(1),
                    slices: geometrySize.get(2) || 1,
                  };

                  setImageInfo(info);
                  // Total slices/frames will be set in renderend from ViewController
                  setCurrentSlice(0);
                  setCurrentFrame(0);

                  if (props.onImageLoad) {
                    props.onImageLoad(info);
                  }
                }
              }
            } catch (err) {
              console.warn("Could not get image info:", err);
            }
          });

          // Listen for position changes (when user scrolls through slices/frames)
          dwvApp.addEventListener("positionchange", (event: any) => {
            try {
              const vl = dwvApp.getViewLayersByDataId(event.dataid)[0];
              const vc = vl.getViewController();
              const currentIndex = vc.getCurrentIndex();

              // Track both dimensions independently
              const sliceIndex = currentIndex.get(2); // Dimension 2 = slices
              const frameIndex = currentIndex.get(3); // Dimension 3 = frames

              if (typeof sliceIndex !== "undefined") {
                setCurrentSlice(sliceIndex);
              }
              if (typeof frameIndex !== "undefined") {
                setCurrentFrame(frameIndex);
              }
            } catch (err) {
              console.warn("Could not get current index:", err);
            }
          });

          dwvApp.addEventListener("loadend", (event: any) => {
            if (nReceivedLoadError) {
              setLoadProgress(0);
              setError(
                "Received errors during load. Check console for details.",
              );
            }
          });

          dwvApp.addEventListener("loaditem", () => {
            nLoadItem++;
          });

          dwvApp.addEventListener("loaderror", (event: any) => {
            console.error("DWV load error:", event.error);
            nReceivedLoadError++;
            setError(event.error?.message || "Failed to load DICOM file");
            setIsLoading(false);

            if (props.onError) {
              props.onError(
                new Error(event.error?.message || "Failed to load DICOM file"),
              );
            }
          });

          // Handle keyboard events
          dwvApp.addEventListener("keydown", (event: any) => {
            dwvApp.defaultOnKeydown(event);
          });

          // Handle window resize
          const handleResize = () => dwvApp.onResize();
          window.addEventListener("resize", handleResize);

          setApp(dwvApp);

          // Load the DICOM file from URL
          dwvApp.loadURLs([props.url]);

          // Return cleanup function
          return () => {
            window.removeEventListener("resize", handleResize);
            if (dwvApp) {
              dwvApp.reset();
            }
            // Clear container content
            if (container) {
              container.innerHTML = "";
            }
          };
        } catch (err) {
          console.error("Failed to initialize DICOM viewer:", err);
          setIsLoading(false);
          const errorMessage =
            err instanceof Error
              ? err.message
              : "Failed to initialize DICOM viewer";
          setError(errorMessage);

          if (props.onError) {
            props.onError(err instanceof Error ? err : new Error(errorMessage));
          }
        }
      };

      const cleanup = initializeViewer();

      // Return cleanup function for useEffect
      return () => {
        if (cleanup && typeof cleanup.then === "function") {
          cleanup.then((cleanupFn: any) => {
            if (typeof cleanupFn === "function") {
              cleanupFn();
            }
          });
        }
      };
    }, [props.url, props.onImageLoad, props.onError]);

    // Add cleanup effect for component unmount
    useEffect(() => {
      return () => {
        if (app) {
          app.reset();
        }
      };
    }, [app]);

    if (error) {
      return (
        <div className="flex items-center justify-center p-8 text-center">
          <div>
            <p className="text-red-600 font-semibold mb-2">
              Error loading DICOM file
            </p>
            <p className="text-gray-600 text-sm">{error}</p>
          </div>
        </div>
      );
    }

    const toolButtons = Object.keys(TOOLS_CONFIG).map((tool) => {
      const toolName = tool as ToolName;
      const isSelected = selectedTool === toolName;
      const isDisabled = !dataLoaded || !canRunTool(toolName);

      return (
        <button
          key={tool}
          onClick={() => handleToolChange(toolName)}
          disabled={isDisabled}
          className={`
            p-2 rounded border transition-colors
            ${
              isSelected
                ? "bg-persian-500 text-white border-persian-500"
                : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }
            ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          `}
          title={tool}
        >
          {getToolIcon(toolName)}
        </button>
      );
    });

    return (
      <div
        style={{
          width: props.width || "100%",
          height:
            props.height || `calc(100vh - ${props.heightOffset || 100}px)`,
          position: "relative",
        }}
        className="flex flex-col"
      >
        {/* Toolbar */}
        {props.showToolbar !== false && props.showTools !== false && (
          <div className="flex items-center gap-2 p-2 bg-black flex-wrap">
            {/* Tool Buttons */}
            <div className="flex gap-1">{toolButtons}</div>

            {/* Action Buttons */}
            <div className="flex gap-1 ml-2">
              <button
                onClick={handleReset}
                disabled={!dataLoaded}
                className="p-2 rounded border bg-white text-gray-700 border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Reset"
              >
                <RefreshCwIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
            <Loading />
          </div>
        )}

        {/* Viewer Container */}
        <div
          ref={containerRef}
          className="flex-1 relative bg-black"
          style={{
            opacity: isLoading ? 0.3 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
        />

        {/* Navigation Pagers */}
        {dataLoaded && (totalSlices > 1 || totalFrames > 1) && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col gap-2">
            {/* Slice Pager (Dimension 2) */}
            {totalSlices > 1 && (
              <div className="bg-black bg-opacity-90 text-white rounded-lg shadow-lg">
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <span className="text-xs font-semibold mr-1 text-gray-300">
                    SLICE
                  </span>
                  <button
                    onClick={handlePreviousSlice}
                    disabled={currentSlice === 0}
                    className="p-1 rounded hover:bg-white hover:bg-opacity-20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Previous slice"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <div className="px-3 text-sm font-medium min-w-[60px] text-center">
                    {currentSlice + 1} / {totalSlices}
                  </div>
                  <button
                    onClick={handleNextSlice}
                    disabled={currentSlice >= totalSlices - 1}
                    className="p-1 rounded hover:bg-white hover:bg-opacity-20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Next slice"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Frame Pager (Dimension 3) */}
            {totalFrames > 1 && (
              <div className="bg-black bg-opacity-90 text-white rounded-lg shadow-lg">
                <div className="flex items-center gap-1 px-2 py-1.5">
                  <span className="text-xs font-semibold mr-1 text-gray-300">
                    FRAME
                  </span>
                  <button
                    onClick={handlePreviousFrame}
                    disabled={currentFrame === 0}
                    className="p-1 rounded hover:bg-white hover:bg-opacity-20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Previous frame"
                  >
                    <ChevronLeftIcon className="w-5 h-5" />
                  </button>
                  <div className="px-3 text-sm font-medium min-w-[60px] text-center">
                    {currentFrame + 1} / {totalFrames}
                  </div>
                  <button
                    onClick={handleNextFrame}
                    disabled={currentFrame >= totalFrames - 1}
                    className="p-1 rounded hover:bg-white hover:bg-opacity-20 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Next frame"
                  >
                    <ChevronRightIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

export default DicomViewer;
